import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  FertilityTestingStackScreenProps,
  type FertilityTestingStackParamList,
} from "../FertilityTestingNavigator";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import {
  TestTube2,
  Stethoscope,
  Syringe,
  Sparkles,
  type LucideIcon,
} from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { getNextTestScreen } from "../getNextTestScreen";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";

type SamplingTestType =
  | "swab_test"
  | "pap_smear"
  | "endometrial_biopsy"
  | "other";

const samplingTests: {
  id: SamplingTestType;
  labelKey: string;
  icon: LucideIcon;
  sex: "AFAB" | "AMAB" | "both";
}[] = [
  {
    id: "swab_test",
    labelKey: "med_history.fertility.testing.sampling_type.swab_test",
    icon: TestTube2,
    sex: "both",
  },
  {
    id: "pap_smear",
    labelKey: "med_history.fertility.testing.sampling_type.pap_smear",
    icon: Stethoscope,
    sex: "AFAB",
  },
  {
    id: "endometrial_biopsy",
    labelKey: "med_history.fertility.testing.sampling_type.endometrial_biopsy",
    icon: Syringe,
    sex: "AFAB",
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.sampling_type.other",
    icon: Sparkles,
    sex: "both",
  },
];

export function SamplingTestsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const navigation =
    useNavigation<
      FertilityTestingStackScreenProps<"SamplingTests">["navigation"]
    >();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();

  // Find existing sampling test results in state
  const samplingResults = state.testResults.filter(
    (tr) => tr.type === "sampling",
  );

  // Map API category to UI type
  const categoryToUI = useMemo<Record<string, SamplingTestType>>(
    () => ({
      swab: "swab_test",
      pap_smear: "pap_smear",
      endometrial_biopsy: "endometrial_biopsy",
      other: "other",
    }),
    [],
  );

  const selected = useMemo<Set<SamplingTestType>>(() => {
    const set = new Set<SamplingTestType>();
    samplingResults.forEach((result) => {
      if (result.samplingTestCategory) {
        const uiType = categoryToUI[result.samplingTestCategory];
        if (uiType) set.add(uiType);
      }
    });
    return set;
  }, [samplingResults, categoryToUI]);

  const handleSelectionChange = (newSelection: SamplingTestType[]) => {
    const categoryMap: Record<SamplingTestType, string> = {
      swab_test: "swab",
      pap_smear: "pap_smear",
      endometrial_biopsy: "endometrial_biopsy",
      other: "other",
    };

    const oldSelection = new Set<SamplingTestType>();
    samplingResults.forEach((result) => {
      if (result.samplingTestCategory) {
        const uiType = categoryToUI[result.samplingTestCategory];
        if (uiType) oldSelection.add(uiType);
      }
    });

    const newSelectionSet = new Set(newSelection);

    // Remove deselected items
    let updatedResults = state.testResults.filter((tr) => {
      if (tr.type !== "sampling") return true;
      const uiType = categoryToUI[tr.samplingTestCategory || ""];
      return uiType && newSelectionSet.has(uiType as SamplingTestType);
    });

    // Add newly selected items
    newSelection.forEach((uiType) => {
      if (!oldSelection.has(uiType)) {
        const newTestResult: import("@louise/medical-history/basic-info/types").TestResultCreate =
          {
            type: "sampling" as const,
            samplingTestCategory: categoryMap[
              uiType
            ] as import("@louise/medical-history/basic-info/types").SamplingTestCategory,
          };
        updatedResults = [...updatedResults, newTestResult];
      }
    });

    updateState({ testResults: updatedResults });
  };

  const handleNext = () => {
    // Increment navigation index and get next screen
    const newIndex = (state.currentNavigationIndex || 0) + 1;
    updateState({ currentNavigationIndex: newIndex });
    const nextScreen = getNextTestScreen({
      ...state,
      currentNavigationIndex: newIndex,
    });
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  const handleToggle = (id: SamplingTestType) => {
    const current = new Set(selected);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    handleSelectionChange(Array.from(current));
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.sampling_title")}
        subtitle={t("med_history.fertility.testing.sampling_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleNext}
        saveDisabled={selected.size === 0}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {samplingTests
            .filter((test) => test.sex === "both" || test.sex === state.sex)
            .map((test) => {
              const isSelected = selected.has(test.id);
              return (
                <View key={test.id} style={styles.optionWrapper}>
                  <SquareOptionButton
                    icon={test.icon}
                    label={t(test.labelKey)}
                    isSelected={isSelected}
                    onPress={() => handleToggle(test.id)}
                    iconSize={20}
                    containerSize={40}
                    labelSize="sm"
                  />
                </View>
              );
            })}
        </View>
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default SamplingTestsScreen;
